import React, { useState, useRef, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { SiMeta } from 'react-icons/si';
import { getRequestWithAuth } from '~/lib/api';

type MetaAdsConnectorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const MetaAdsConnectorModal: React.FC<MetaAdsConnectorModalProps> = ({
  open,
  onOpenChange,
}) => {
  const modalRef = useRef<HTMLDialogElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && modalRef.current) {
      modalRef.current?.showModal();
    } else if (!open && modalRef.current) {
      modalRef.current?.close();
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setIsLoading(false);
      setError(null);
    }
  }, [open]);

  const handleConnect = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { authorization_url } = await getRequestWithAuth(
        '/api/v1/meta-ads/authorize'
      );
      // Redirect to Meta OAuth
      window.location.href = authorization_url;
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.detail || 'Failed to start Meta authorization';
      setError(errorMsg);
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-lg">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-xl">Connect Meta Ads</h3>
          <button
            onClick={handleCancel}
            className="btn btn-sm btn-circle btn-ghost"
          >
            <IoClose size={20} />
          </button>
        </div>

        <p className="text-sm text-base-content/70 mb-6">
          Connect your Meta (Facebook) Ads account to sync your advertising data
          and get insights on your ad performance.
        </p>

        {/* Info box */}
        <div className="bg-base-200 rounded-lg p-4 mb-6">
          <h4 className="font-medium mb-2">What you'll get:</h4>
          <ul className="text-sm text-base-content/70 space-y-1">
            <li>• Campaign, ad set, and ad performance data</li>
            <li>• Audience insights by age, gender, and location</li>
            <li>• Creative performance metrics</li>
            <li>• Automatic data sync every 24 hours</li>
          </ul>
        </div>

        {/* Permissions info */}
        <div className="text-xs text-base-content/60 mb-6">
          <p>
            We'll request read-only access to your ad accounts. You can revoke
            access anytime from your Meta Business Settings.
          </p>
        </div>

        {/* Error display */}
        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {/* Connect button */}
        <button
          type="button"
          onClick={handleConnect}
          className="btn btn-primary w-full gap-2"
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="loading loading-spinner"></span>
          ) : (
            <>
              <SiMeta size={18} />
              Connect with Meta
            </>
          )}
        </button>

        <p className="text-xs text-center opacity-70 mt-4">
          You'll be redirected to Meta to authorize access
        </p>
      </div>
    </dialog>
  );
};

export default MetaAdsConnectorModal;
